import { ArrowRight, MapPin } from "lucide-react";
import AbstractShape from "@/components/AbstractShape";
import { goToIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma",
  "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
];

function SectionHeading({ children, testId }) {
  return (
    <h2
      data-testid={testId}
      className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-[#2C2B29] leading-tight"
    >
      {children}
    </h2>
  );
}

function Para({ children }) {
  return <p className="mt-3 text-[#5E5C58] leading-relaxed">{children}</p>;
}

export default function ServiceAreas() {
  useSEO({
    title: "Service Areas | Available in All 50 States | Melbi Wellness",
    description:
      "Melbi Wellness serves eligible patients in all 50 U.S. states and DC through licensed clinicians and pharmacy partners. State-specific availability applies.",
    url: "https://melbiwellness.com/service-areas",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Service Areas", url: "/service-areas" },
      ]),
    ],
  });
  return (
    <div data-testid="service-areas-page" className="bg-[#FAF6EF]">
      {/* HERO */}
      <section className="relative">
        <AbstractShape
          variant="gradient-warm"
          colors={["#F5E6D3", "#E8D5C4", "#D4DCD7"]}
          className="absolute inset-0"
          testId="service-areas-hero-bg"
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-6 md:pt-24 md:pb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Service Areas
          </p>
          <h1
            data-testid="service-areas-h1"
            className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2C2B29] leading-[1.1] max-w-3xl"
          >
            Where{" "}
            <span className="italic text-[#C96A4E]">Melbi is available.</span>
          </h1>
          <p className="mt-5 text-[#5E5C58] leading-relaxed max-w-2xl">
            Melbi Wellness partners with a nationwide network of licensed
            clinicians and pharmacy partners to deliver clinician-guided
            wellness care to eligible patients across the United States.
          </p>
        </div>
      </section>

      {/* ARTICLE BODY */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-8 pb-10 md:pb-14 space-y-10 md:space-y-12">
        {/* Nationwide coverage */}
        <section>
          <SectionHeading testId="section-nationwide">
            Nationwide telemedicine coverage
          </SectionHeading>
          <Para>
            Services are available in all 50 U.S. states and the District of
            Columbia through affiliated medical providers licensed to practice
            in each jurisdiction, and through pharmacy partners authorized to
            dispense medications in accordance with applicable state licensure
            requirements.
          </Para>
          <Para>
            Whether you live in a major metropolitan area or a rural community,
            Melbi is designed to be accessible from anywhere in the country
            with an internet connection.
          </Para>
          <div
            data-testid="service-areas-legitscript-notice"
            className="mt-6 bg-white border border-[#E8E5DF] rounded-2xl p-5 md:p-6"
          >
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8A9A92]">
              Jurisdictional disclosure
            </p>
            <p className="mt-3 text-[#5E5C58] leading-relaxed text-sm md:text-base">
              Melbi Wellness serves patients in all 50 U.S. states and the
              District of Columbia through affiliated licensed medical
              providers and authorized pharmacy partners. The jurisdictions we
              serve are in accordance with LegitScript Healthcare Certification
              Standards 5 and 8.
            </p>
          </div>
        </section>

        {/* State grid */}
        <section
          data-testid="service-areas-state-grid"
          className="bg-white border border-[#E8E5DF] rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)] overflow-hidden"
        >
          <details
            data-testid="service-areas-state-list-details"
            className="group"
          >
            <summary
              data-testid="service-areas-state-list-toggle"
              className="flex items-center justify-between gap-3 px-5 md:px-7 py-5 md:py-6 cursor-pointer list-none select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C96A4E]/30 hover:bg-[#FAF9F6]/60 transition-colors"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
                  All 50 states + D.C.
                </p>
                <h3 className="mt-1 font-display text-lg md:text-xl font-semibold tracking-tight text-[#2C2B29] leading-tight">
                  View states we serve
                </h3>
              </div>
              <span
                aria-hidden="true"
                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#E8E5DF] text-[#5E5C58] text-[13px] leading-none transition-transform duration-200 group-open:rotate-180 shrink-0"
              >
                ▾
              </span>
            </summary>
            <ul className="px-5 md:px-7 pb-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2.5 text-sm md:text-[15px] text-[#2C2B29]">
              {STATES.map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <MapPin size={14} className="mt-1 text-[#C96A4E] shrink-0" />
                  <span className="leading-snug">{s}</span>
                </li>
              ))}
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 text-[#C96A4E] shrink-0" />
                <span className="leading-snug">District of Columbia</span>
              </li>
            </ul>
          </details>
        </section>

        {/* State-specific variation */}
        <section>
          <SectionHeading testId="section-state-variation">
            State-specific variation
          </SectionHeading>
          <Para>
            Certain medications, formulations, program lengths, refill cycles,
            or fulfillment options may vary depending on state-specific
            pharmacy regulations, clinical practice rules, and dispensing
            restrictions.
          </Para>
          <Para>
            During your secure online health intake, a licensed clinician
            reviews the information you provide alongside the requirements of
            the state in which you are located. If a specific treatment or
            formulation is not available in your state, the clinician may
            recommend an alternative, request additional information, or
            decline treatment when it is not clinically or legally appropriate.
          </Para>
        </section>

        {/* Territories */}
        <section>
          <SectionHeading testId="section-territories">
            U.S. territories and international patients
          </SectionHeading>
          <Para>
            At this time, Melbi services are limited to patients physically
            located within the 50 United States and the District of Columbia
            at the time of intake and treatment.
          </Para>
          <Para>
            Melbi is not currently available to patients located in Puerto
            Rico, the U.S. Virgin Islands, Guam, American Samoa, the Northern
            Mariana Islands, or outside the United States. Availability in
            these areas may change in the future as regulatory and pharmacy
            fulfillment pathways evolve.
          </Para>
        </section>

        {/* How eligibility is determined */}
        <section>
          <SectionHeading testId="section-how-eligibility">
            How location is verified
          </SectionHeading>
          <Para>
            When you begin your intake, you will be asked to confirm your
            state of residence and the state in which you will be physically
            located at the time of any live clinical interaction, if
            applicable.
          </Para>
          <Para>
            Prescriptions, when appropriate, are issued by a licensed clinician
            in the patient&apos;s state and fulfilled by a licensed pharmacy
            partner authorized to ship medications to that state. Melbi does
            not dispense or manufacture medication.
          </Para>
        </section>

        {/* Disclaimer */}
        <p
          data-testid="service-areas-disclaimer"
          className="text-xs md:text-sm text-[#8A9A92] leading-relaxed border-t border-[#E8E5DF] pt-6"
        >
          Service availability is subject to change based on state licensure
          requirements, pharmacy partner authorizations, applicable laws and
          regulations, and clinical appropriateness. Approval is never
          guaranteed and not all patients qualify. Compounded medications, if
          prescribed, are not FDA-approved.
        </p>
      </article>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#F5E6D3] via-[#F3F1EC] to-[#E8D5C4]"
          aria-hidden="true"
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2C2B29] leading-[1.1]">
            Ready when{" "}
            <span className="italic text-[#C96A4E]">you are.</span>
          </h2>
          <p className="mt-4 text-[#5E5C58] text-base md:text-lg leading-relaxed max-w-xl mx-auto">
            Available in all 50 states through licensed clinicians and pharmacy
            partners.
          </p>
          <button
            type="button"
            data-testid="service-areas-start-now-cta"
            onClick={goToIntake}
            className="mt-7 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-3.5 text-sm font-semibold hover:bg-[#B35A40] transition-all shadow-[0_8px_24px_rgba(201,106,78,0.25)] hover:shadow-[0_12px_32px_rgba(201,106,78,0.35)] hover:-translate-y-0.5"
          >
            Start now
            <ArrowRight size={16} className="ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
}
